import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { devActions } from "../../../redux/actions";
import TreeView, { flattenTree } from "react-accessible-treeview";
import "./ProjectExplorer.css";
import { devModeConfig } from "../../components/DevMode.helpers";
import { NodeRenderer } from "./NodeRenderer";
import * as immutable from 'object-path-immutable'
import { wait } from "../../../redux/utils";
import { IoClose, IoFilterOutline } from "react-icons/io5";

const createFallbackExplorerData = () => ({
  name: "ROOT",
  id: "ROOT",
  isBranch: true,
  children: [
    {
      name: devModeConfig.captions.OPEN_TABS,
      id: devModeConfig.ids.OPEN_TABS,
      isBranch: true,
      children: [],
    },
    {
      name: devModeConfig.captions.APP,
      id: devModeConfig.ids.APP,
      isBranch: true,
      children: [
        {
          name: devModeConfig.template.explorerCaption,
          id: devModeConfig.template.idFolder,
          children: [],
          isBranch: true,
        },
        {
          name: devModeConfig.primitiva.explorerCaption,
          id: devModeConfig.primitiva.idFolder,
          children: [],
          isBranch: true,
        },
        {
          name: devModeConfig.operacion.explorerCaption,
          id: devModeConfig.operacion.idFolder,
          children: [],
          isBranch: true,
        },
      ],
    },
  ],
});

const isValidTreeRoot = (data) =>
  Boolean(
    data &&
      typeof data === "object" &&
      (typeof data.id === "string" || typeof data.id === "number") &&
      Array.isArray(data.children)
  );

const isValidFlatTreeNode = (node) =>
  Boolean(
    node &&
      (typeof node.id === "string" || typeof node.id === "number")
  );

const normalizeFlatTreeData = (data) => {
  if (!Array.isArray(data)) return [];
  const nodes = data
    .filter(isValidFlatTreeNode)
    .map((node) => ({
      ...node,
      children: Array.isArray(node.children) ? node.children.slice() : [],
    }));
  if (!nodes.length) return [];
  const idsSet = new Set(nodes.map((node) => node.id));
  const normalized = nodes.map((node) => ({
    ...node,
    children: node.children.filter((childId) => idsSet.has(childId)),
  }));
  if (normalized.some((item) => item.parent === null)) return normalized;
  const rootIndex = normalized.findIndex((item) => item.id === "ROOT");
  if (rootIndex === -1) return [];
  const rootNode = normalized[rootIndex];
  const withRoot = normalized.slice();
  withRoot[rootIndex] = { ...rootNode, parent: null };
  return withRoot;
};

const filterTreeNode = (node, searchText, keepNode = false) => {
  if (!node) return null;
  const originalChildren = Array.isArray(node.children) ? node.children : [];
  const children = originalChildren
    .map((child) => filterTreeNode(child, searchText))
    .filter(Boolean);
  const nodeName = typeof node.name === "string" ? node.name.toLowerCase() : "";
  if (keepNode) {
    return { ...node, children };
  }
  const isBranch = Boolean(node.isBranch) || originalChildren.length > 0;
  if (isBranch) {
    // Las carpetas solo se muestran si tienen elementos coincidentes.
    if (children.length > 0) {
      return { ...node, children };
    }
    return null;
  }
  const selfMatch = nodeName.includes(searchText);
  if (selfMatch) {
    return { ...node, children };
  }
  return null;
};

const collectExpandedBranchIds = (node) => {
  if (!node) return [];
  const children = Array.isArray(node.children) ? node.children : [];
  const next = [];
  if (children.length > 0) {
    next.push(node.id);
    children.forEach((child) => {
      next.push(...collectExpandedBranchIds(child));
    });
  }
  return next;
};

export const ProjectExplorer = () => {

  const explorerData = useSelector(state => state.dev.explorer.data);
  const treeData = useSelector(state => state.dev.explorer.treeData);
  const templates = useSelector(state => state.dev.templates);
  const primitivas = useSelector(state => state.dev.primitivas);
  const operaciones = useSelector(state => state.dev.operaciones);
  const tabs = useSelector(state => state.dev.tabs);
  const dispatch = useDispatch();
  const treeRef = useRef();
  const filterLoadRequestRef = useRef({
    templates: false,
    primitivas: false,
    operaciones: false,
  });

  const selectedId = useSelector(state => state.dev.explorer.selectedId);
  const expandedIds = useSelector(state => state.dev.explorer.expandedIds);
  const [filterText, setFilterText] = useState("");
  const normalizedFilter = useMemo(() => filterText.trim().toLowerCase(), [filterText]);
  const fallbackExplorerData = useMemo(() => createFallbackExplorerData(), []);
  const safeExplorerData = useMemo(
    () => (isValidTreeRoot(explorerData) ? explorerData : fallbackExplorerData),
    [explorerData, fallbackExplorerData]
  );
  const selectedIds = useMemo(
    () => (selectedId === undefined || selectedId === null ? [] : [selectedId]),
    [selectedId]
  );
  const safeExpandedIds = useMemo(() => (Array.isArray(expandedIds) ? expandedIds : []), [expandedIds]);
  const baseTreeData = useMemo(
    () => {
      const normalizedTreeData = normalizeFlatTreeData(treeData);
      if (normalizedTreeData.length > 0) return normalizedTreeData;
      return flattenTree(safeExplorerData);
    },
    [safeExplorerData, treeData]
  );

  const filteredState = useMemo(() => {
    if (!normalizedFilter) {
      return {
        treeDataToRender: baseTreeData,
        expandedIdsToRender: safeExpandedIds,
      };
    }
    const filteredRoot = filterTreeNode(safeExplorerData, normalizedFilter, true) || {
      ...safeExplorerData,
      children: [],
    };
    const expandedBranchIds = collectExpandedBranchIds(filteredRoot).filter((id) => id && id !== "ROOT");
    return {
      treeDataToRender: flattenTree(filteredRoot),
      expandedIdsToRender: expandedBranchIds,
    };
  }, [baseTreeData, normalizedFilter, safeExpandedIds, safeExplorerData]);
  const visibleNodeIds = useMemo(
    () => new Set((filteredState.treeDataToRender || []).map((item) => item.id)),
    [filteredState.treeDataToRender]
  );
  const selectedIdsToRender = useMemo(
    () => selectedIds.filter((id) => id !== undefined && id !== null && visibleNodeIds.has(id)),
    [selectedIds, visibleNodeIds]
  );
  const expandedIdsToRender = useMemo(
    () => (filteredState.expandedIdsToRender || []).filter((id) => visibleNodeIds.has(id)),
    [filteredState.expandedIdsToRender, visibleNodeIds]
  );
  const isFiltering = normalizedFilter.length > 0;
  const filteredTreeSignature = useMemo(
    () => (filteredState.treeDataToRender || []).map((item) => String(item.id)).join("|"),
    [filteredState.treeDataToRender]
  );
  const treeViewKey = isFiltering
    ? `filtered:${normalizedFilter}:${filteredTreeSignature}`
    : "default";

  useEffect(() => {
    //if(!treeRef.current) return;
    //if(!treeRef.current?.parentElement) return;
    //console.log("treeRef", treeRef.current?.parentElement);
    //console.log("treeRef events", getEventosRegistrados(treeRef.current?.parentElement));
  }, [treeRef.current])

  useEffect(() => {
    console.log("update templates", {templates});
    dispatch(devActions.updateData(devModeConfig.template.explorerCaption, templates, devModeConfig.template.type));
  }, [templates])

  useEffect(() => {
    console.log("update primitivas", {primitivas});
    dispatch(devActions.updateData(devModeConfig.primitiva.explorerCaption, primitivas, devModeConfig.primitiva.type));
  }, [primitivas])

  useEffect(() => {
    console.log("update operaciones", {operaciones});
    dispatch(devActions.updateData(devModeConfig.operacion.explorerCaption, operaciones, devModeConfig.operacion.type));
  }, [operaciones])

  useEffect(() => {
    dispatch(devActions.updateDataOpenFiles());
  }, [tabs])

  useEffect(() => {
    if (!normalizedFilter) {
      filterLoadRequestRef.current.templates = false;
      filterLoadRequestRef.current.primitivas = false;
      filterLoadRequestRef.current.operaciones = false;
      return;
    }
    if ((!Array.isArray(templates) || templates.length === 0) && !filterLoadRequestRef.current.templates) {
      filterLoadRequestRef.current.templates = true;
      dispatch(devActions.getTemplates());
    }
    if ((!Array.isArray(primitivas) || primitivas.length === 0) && !filterLoadRequestRef.current.primitivas) {
      filterLoadRequestRef.current.primitivas = true;
      dispatch(devActions.getPrimitivas());
    }
    if ((!Array.isArray(operaciones) || operaciones.length === 0) && !filterLoadRequestRef.current.operaciones) {
      filterLoadRequestRef.current.operaciones = true;
      dispatch(devActions.getOrquestadores());
    }
  }, [dispatch, normalizedFilter, operaciones, primitivas, templates]);

  const wrappedOnLoadData = async ({
    element,
    isExpanded,
    isSelected,
    isDisabled,
    isHalfSelected,
    treeState
  }) => {
    if (normalizedFilter) return;
    if(element.name === devModeConfig.template.explorerCaption && element.children.length === 0 && isExpanded){
      dispatch(devActions.getTemplates())
      dispatch(devActions.getOrquestadores())
      dispatch(devActions.getPrimitivas())
    };
    if(element.name === devModeConfig.operacion.explorerCaption && element.children.length === 0 && isExpanded){
      dispatch(devActions.getOrquestadores())
      dispatch(devActions.getTemplates())
      dispatch(devActions.getPrimitivas())
    };
    if(element.name === devModeConfig.primitiva.explorerCaption && element.children.length === 0 && isExpanded){
      dispatch(devActions.getPrimitivas())
      dispatch(devActions.getOrquestadores())
      dispatch(devActions.getTemplates())
    };
  };
  
  const handleNodeSelect = ({element}) => {
    dispatch(devActions.selectNodeFile(element.id));
    if(!element.isBranch){
      dispatch(devActions.openTab(element));;
    }
  }

  const handleOnClose = (element) => {
    const tab = tabs.find(tab => tab.data.id === element.id.split("/")[0]);
    console.log("handleOnClose", {element, tabs, tab});
    closeTab(tab);
  }
  
  const closeTab = (tab) => {
    return dispatch(devActions.closeTab(tab));
  }

  const closeAllTabs = (tabsList) => {
    const tabIndex = tabsList.findIndex(item => !item.intented);
    const item = tabsList.find(item => !item.intented);
    console.log("closeAllTabs", {tabsList, tabIndex, item});
    if(!item) return;
    closeTab(item.tab)
      .then(result => {
        closeAllTabs(immutable.set(tabsList, `${tabIndex}.intented`, true));
      })
      .catch(error => {})
  }

  const handleOnCloseAll = (element, tabsCount = tabs.length, tabsIntented = 0) => {
    const tabsList = tabs.map(tab => ({intented: false, tab}));
    closeAllTabs(tabsList);
  }


  const handleOnRemove = (element) => {
    const id = element.metadata?.item?.id;
    const type = element.metadata?.type;
    if(!type) return;
    dispatch(devActions.removeItem(element))
  }

  const handleAddNodeClick = (folderElement, type) => {
    dispatch(devActions.createNewFile(folderElement, type));
  }

  const handleOnCancelNewFile = (id) => {
    dispatch(devActions.cancelNewFile(id));
  }

  const handleOnConfirmNewFile = (id, name, type) => {
    console.log("handleOnConfirmNewFile", new Date())
    dispatch(devActions.confirmNewFile(id, name, type))
    .then(newId => {
      dispatch(devActions.selectNodeFile(`${newId}-${type}`))
      return wait(50, newId);
    })
    .then(newId => {
      dispatch(devActions.openTab(`${newId}-${type}`))
    })
    
    ;
  }

  const changedIds = useMemo(() => {
    return tabs.filter(tab => tab.changed).map(tab => tab.data.id);
  }, [tabs]);

  const handleOnExpanded = ({
    element,
    isExpanded,
    isSelected,
    isHalfSelected,
    isDisabled,
    treeState,
  } ) => {
    if (normalizedFilter) return;
    console.log("handleOnExpanded",{element, isExpanded, isSelected, isHalfSelected, isDisabled, treeState})
    dispatch(devActions.setExpandedFolder(element.id, isExpanded ));
  }

  return (
    <div className="ide">
      <div className="ide-filter">
        <IoFilterOutline className="ide-filter-icon" />
        <input
          type="text"
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder="Filtrar..."
          className="ide-filter-input"
          aria-label="Filtrar explorador"
        />
        {filterText ? (
          <button
            type="button"
            className="ide-filter-clear"
            onClick={() => setFilterText("")}
            aria-label="Limpiar filtro"
          >
            <IoClose />
          </button>
        ) : null}
      </div>
      <div className="ide-tree-wrapper">
      <TreeView
        key={treeViewKey}
        selectedIds={isFiltering ? undefined : selectedIdsToRender}
        expandedIds={isFiltering ? undefined : expandedIdsToRender}
        defaultExpandedIds={isFiltering ? expandedIdsToRender : undefined}
        onExpand={handleOnExpanded}
        ref={treeRef}
        onLoadData={wrappedOnLoadData}
        data={filteredState.treeDataToRender}
        aria-label="directory tree"
        togglableSelect
        clickAction="EXCLUSIVE_SELECT"
        nodeRenderer={
          (props) => <NodeRenderer 
            {...props} 
            onClose={handleOnClose} 
            onCloseAll={handleOnCloseAll} 
            onCancelNewFile={handleOnCancelNewFile} 
            onConfirmNewFile={handleOnConfirmNewFile} 
            onRemove={handleOnRemove}
            changedIds={changedIds} 
            onAddNodeClick={handleAddNodeClick}/>
        }
        onNodeSelect={handleNodeSelect}
        expandOnKeyboardSelect={false}
      />
      </div>
    </div>
  );
}
